import * as React from 'react';
import { connect } from 'react-redux';
import bindAllOfThis from "src/utils/BindThisHelper"; // 'this' binder
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';

import Button from 'antd/lib/button/button';
import { message, Form, Input, Row, Col, Select, AutoComplete, DatePicker, Divider } from 'antd';
import { NameOf } from 'src/utils/NameOf';
import { FormComponentProps } from 'antd/lib/form';
import moment from 'moment';
import { Roles } from 'src/constants/Roles';
import KeyValueDTO from 'src/models/generic/KeyValueDTO';
import { Grades } from 'src/constants/Grades';
import ArrayUtil from 'src/utils/ArrayUtil';
import UpdateUserDTO from 'src/models/user/UpdateUserDTO';
import Languages from 'src/constants/Languages';
import FieldDecorator from 'src/components/common/form/FieldDecorator';
import { ReduxStoreModel } from 'src/redux/ReduxModel';
import { UserState } from 'src/redux/UserReducer';
import { SelectValue } from 'antd/lib/select';
import EmulationDTO from 'src/models/account/EmulationDTO';

interface AddEditUserFormProps extends FormComponentProps, LocalizeContextProps {
    userData: Partial<UpdateUserDTO>;
    emulationInfo: Partial<EmulationDTO>;
    schools: KeyValueDTO<number, string>[];
    isAdmin: boolean;

    isSubmitting: boolean;

    onSubmit: (values: UpdateUserDTO) => void;
    onCancel: () => void;
    onRoleChange: (roleId: number) => void;

    // From Redux
    User: UserState;
}

interface AddEditUserFormState {
    filteredSchools: string[];
    filteredGrades: string[];
    filteredCultures: string[];
}

class AddEditUserForm extends React.Component<AddEditUserFormProps, AddEditUserFormState> {
    constructor(props: AddEditUserFormProps) {
        super(props);
        bindAllOfThis(this, AddEditUserForm.prototype);

        this.state = {
            filteredSchools: [],
            filteredGrades: [],
            filteredCultures: [],
        };
    }

    private handleFilterSchools(value: string) {
        let input = (value || "").toLowerCase();
        let items =this.props.schools;
        let filteredSchools: string[] = [];

        // Filter the clients if input is 3+ chars OR if the number of schools is small, like 50 or so
        if (input.length > 2 || items.length < 50) {
            filteredSchools = items
                .filter(x => x.value.toLowerCase().indexOf(input) >= 0)
                .map(x => x.value);
            filteredSchools = ArrayUtil.Unique(filteredSchools);
        }

        this.setState({ filteredSchools });
    }

    private handleFilterGrades(value: string) {
        let input = (value || "").toLowerCase();
        let items = Grades.All;
        let filteredGrades: string[] = [];

        // Filter the clients if input is 3+ chars OR if the number of schools is small, like 50 or so
        if (input.length > 2 || items.length < 50) {
            filteredGrades = items
                .filter(x => x.name.toLowerCase().indexOf(input) >= 0)
                .map(x => x.shortName);
            filteredGrades = ArrayUtil.Unique(filteredGrades);
        }

        this.setState({ filteredGrades });
    }

    private handleFilterCultures(value: string) {
        let input = (value || "").toLowerCase();
        let items = Languages.All;
        let filteredCultures: string[] = [];

        // Filter the clients if input is 3+ chars OR if the number of schools is small, like 50 or so
        if (input.length > 2 || items.length < 50) {
            filteredCultures = items
                .filter(x => x.name.toLowerCase().indexOf(input) >= 0)
                .map(x => `${x.name} (${x.code})`);
            filteredCultures = ArrayUtil.Unique(filteredCultures);
        }

        this.setState({ filteredCultures });
    }

    private handleCancel() {
        this.props.onCancel && this.props.onCancel();
    }

    private onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        this.props.form.validateFields((errors, values: UpdateUserDTO) => {
            let newValues = { ...this.props.userData, ...values } as UpdateUserDTO;
            if (!errors) {
                this.props.onSubmit && this.props.onSubmit(newValues);
            }
        });
    }

    private onRoleChange(value: SelectValue, option: React.ReactElement<any>) {
        this.props.onRoleChange && this.props.onRoleChange(Number(value));
    }

    render() {
        let userData = this.props.userData != null ? this.props.userData : {} as UpdateUserDTO;
        let emulateData = this.props.emulationInfo != null ? this.props.emulationInfo : {} as EmulationDTO;
        let userRole = Roles.FindById(this.props.form.getFieldValue(NameOf<UpdateUserDTO>("roleId")) || userData.roleId);
        let currentUserRole = Roles.FindById(this.props.User.roleID);
        let emulatedUserRole = Roles.FindById(emulateData.roleId);
        let isAdmin = this.props.isAdmin;
        let isEdit = userData.userId > 0;
        let isClever = this.props.User.isSsoEnabled;
        let isEmulating = this.props.emulationInfo != null;

        // Determine what to show and when!
        let roleVisible = false;
        let statusVisible = false;
        let schoolVisible = false;
        let roleEdit = false;
        let statusEdit = false;
        let schoolEdit = false;
        let roles = [];

        // Dev Note: This is not as condensed as it could be but should be much more readable and MUCH easier to modify in the future
        switch (currentUserRole) {
            case Roles.KuderAdmin:
                // When adding. Or, when editing a user that is an admin but not a KuderAdmin
                roleVisible = isEdit && isAdmin && ![Roles.KuderAdmin, Roles.StateAdmin, Roles.Student].some(x => x === userRole);
                statusVisible = isEdit;
                // When adding or editing a user that can have a school assigned to it
                schoolVisible = [Roles.SchoolAdmin, Roles.TeacherCounselorAdmin, Roles.Student].some(x => x === userRole);

                roleEdit = ![Roles.KuderAdmin, Roles.Student].some(x => x === userRole);
                statusEdit = isEdit;
                // When selecting a particular admin OR when a student does not have a school attached to them
                schoolEdit = [Roles.SchoolAdmin, Roles.TeacherCounselorAdmin].some(x => x === userRole) && !(userRole === Roles.Student && userData.schoolId != null);

                roles = [Roles.DistrictAdmin, Roles.SchoolAdmin, Roles.TeacherCounselorAdmin];
                break;
            case Roles.StateAdmin:
                // Dev Note: Currently, StateAdmins are not allowed to touch roles or school
                // roleVisible = isEdit && [Roles.DistrictAdmin, Roles.SchoolAdmin, Roles.TeacherCounselorAdmin].some(x => x === userRole);
                // schoolVisible = isEdit && [Roles.SchoolAdmin, Roles.TeacherCounselorAdmin].some(x => x === userRole);
                // schoolEdit = !isEdit || isEdit && userRole !== Roles.Student;
                // roles = [...Roles.All.filter(x => x.isAdmin && x !== Roles.KuderAdmin)];
                roleVisible = false;
                statusVisible = isEdit;
                schoolVisible = false;

                roleEdit = false;
                statusEdit = isEdit;
                schoolEdit = false;

                roles = [Roles.StateAdmin];
                break;
            case Roles.DistrictAdmin:
                if (!isClever) {
                    roleVisible = isEdit && isAdmin;
                    statusVisible = isEdit;
                    schoolVisible = isEdit && [Roles.SchoolAdmin, Roles.TeacherCounselorAdmin, Roles.Student].some(x => x === userRole) || !isEdit && !isAdmin;

                    roleEdit = isAdmin;
                    statusEdit = isEdit;
                    // When selecting a particular admin OR when a student does not have a school attached to them
                    schoolEdit = isEdit && ([Roles.SchoolAdmin, Roles.TeacherCounselorAdmin].some(x => x === userRole) || userRole === Roles.Student && userData.schoolId != null)
                        || !isEdit && !isAdmin;

                    roles = [Roles.DistrictAdmin, Roles.SchoolAdmin, Roles.TeacherCounselorAdmin];
                } else {
                    roleVisible = isEdit && isAdmin;
                    statusVisible = isEdit && isAdmin;
                    schoolVisible = [Roles.SchoolAdmin, Roles.TeacherCounselorAdmin, Roles.Student].some(x => x === userRole) || !isAdmin;

                    roleEdit = isEdit && isAdmin;
                    statusEdit = isEdit && isAdmin;
                    // When selecting a particular admin OR when a student does not have a school attached to them
                    schoolEdit = [Roles.SchoolAdmin].some(x => x === userRole);

                    // Clever DAs cannot add or edit teacher roles to or from
                    roles = [Roles.DistrictAdmin, Roles.SchoolAdmin];
                }
                break;
            case Roles.SchoolAdmin:
                if (!isClever) {
                    roleVisible = isAdmin;
                    statusVisible = isEdit;
                    schoolVisible = isEdit && isAdmin && isEmulating;

                    roleEdit = isAdmin;
                    statusEdit = isEdit;
                    schoolEdit = isEdit && isEmulating;

                    roles = [Roles.SchoolAdmin, Roles.TeacherCounselorAdmin];
                } else {
                    roleVisible = isAdmin && isEmulating;
                    statusVisible = isEdit && isAdmin;
                    schoolVisible = isEdit && isAdmin && isEmulating;

                    roleEdit = isAdmin;
                    statusEdit = isEdit;
                    schoolEdit = isEdit && isEmulating;

                    roles = [Roles.SchoolAdmin];
                }

                // When emulating and editing, the user will be able to upgrade the user to DA
                if (isEmulating && isEdit) {
                    roles = [Roles.DistrictAdmin, ...roles];
                }
                break;
            case Roles.TeacherCounselorAdmin:
                roleVisible = false;
                statusVisible = !isAdmin;
                schoolVisible = false;

                roleEdit = false;
                statusEdit = !isAdmin;
                schoolEdit = false;
                roles = [Roles.TeacherCounselorAdmin];
                break;
            default:
                // Students
                break;
        }

        // I don't think we should allow the user to disable themselves
        if (userData.userId === this.props.User.userId) {
            statusEdit = false;
        }

        return (
            <Form className="admin-form" onSubmit={this.onSubmit}>
                <FieldDecorator
                    form={this.props.form}
                    label={<Translate id="admin.addEditUser.formItem1" />}
                    name={NameOf<UpdateUserDTO>("firstName")}
                    initialValue={userData.firstName}
                    rules={[
                        { required: true, message: <Translate id="admin.common.requiredRule" /> },
                    ]}
                >
                    <Input autoComplete="off" />
                </FieldDecorator>
                <FieldDecorator
                    form={this.props.form}
                    label={<Translate id="admin.addEditUser.formItem2" />}
                    name={NameOf<UpdateUserDTO>("lastName")}
                    initialValue={userData.lastName}
                    rules={[
                        { required: true, message: <Translate id="admin.common.requiredRule" /> },
                    ]}
                >
                    <Input autoComplete="off" />
                </FieldDecorator>
                <FieldDecorator
                    form={this.props.form}
                    label={<Translate id="admin.addEditUser.formItem3" />}
                    name={NameOf<UpdateUserDTO>("email")}
                    initialValue={userData.email}
                    rules={[
                        { required: true, message: <Translate id="admin.common.requiredRule" /> },
                        { type: 'email', message: <Translate id="admin.common.emailRule" /> }
                    ]}
                >
                    <Input autoComplete="off" type="email" />
                </FieldDecorator>

                <FieldDecorator
                    form={this.props.form}
                    label={<Translate id="admin.addEditUser.formItem7" />}
                    name={NameOf<UpdateUserDTO>("studentId")}
                    initialValue={userData.studentId}
                    visible={!this.props.isAdmin}
                >
                    <Input autoComplete="off" />
                </FieldDecorator>
                <FieldDecorator
                    form={this.props.form}
                    label={<Translate id="admin.addEditUser.formItem8" />}
                    name={NameOf<UpdateUserDTO>("dateOfBirth")}
                    initialValue={userData.dateOfBirth ? moment(userData.dateOfBirth, 'YYYY-MM-DD') : null}
                    visible={!this.props.isAdmin}
                >
                    <DatePicker format={"M/D/YYYY"} />
                </FieldDecorator>

                <FieldDecorator
                    form={this.props.form}
                    label={<Translate id="admin.addEditUser.formItem4" />}
                    name={NameOf<UpdateUserDTO>("gradeId")}
                    initialValue={userData.gradeId}
                    rules={[
                        { required: true, message: <Translate id="admin.common.requiredRule" /> },
                    ]}
                    visible={!this.props.isAdmin}
                >
                    <Select optionFilterProp="title" allowClear={true}>
                        {Grades.All.map(x => <Select.Option key={`grade${x.id}`} value={x.id}>{x.shortName}</Select.Option>)}
                    </Select>
                </FieldDecorator>

                <FieldDecorator
                    form={this.props.form}
                    label={<Translate id="admin.addEditUser.formItem6" />}
                    name={NameOf<UpdateUserDTO>("schoolId")}
                    initialValue={userData.schoolId}
                    rules={[
                        { required: true, message: <Translate id="admin.common.requiredRule" /> },
                    ]}
                    visible={schoolVisible}
                >
                    <Select allowClear={true} disabled={!schoolEdit}>
                        {this.props.schools.map(x => <Select.Option key={`school${x.key}`} value={x.key}>{x.value}</Select.Option>)}
                    </Select>
                </FieldDecorator>

                <FieldDecorator
                    form={this.props.form}
                    label={<Translate id="report.admin.roleFilter.placeholder" />}
                    name={NameOf<UpdateUserDTO>("roleId")}
                    initialValue={userData.roleId}
                    rules={[
                        { required: true, message: <Translate id="admin.common.requiredRule" /> },
                    ]}
                    visible={roleVisible}
                >
                    <Select optionFilterProp="title" allowClear={true} disabled={!roleEdit} onSelect={this.onRoleChange}>
                        {roles.map(x => <Select.Option key={`role${x.id}`} value={x.id}>{x.display}</Select.Option>)}
                    </Select>
                </FieldDecorator>

                {/* <AutoComplete
                    allowClear
                    onChange={this.handleFilterSchools}
                    {...{ onFocus: this.handleFilterSchools }} // Hacky because antd types are old, but it is there
                >
                    {this.state.filteredSchools.map(x => <AutoComplete.Option key={x}>{x}</AutoComplete.Option>)}
                </AutoComplete> */}

                <FieldDecorator
                    form={this.props.form}
                    label={<Translate id="admin.addEditClient.formItem5" />}
                    name={NameOf<UpdateUserDTO>("currentCultureId")}
                    initialValue={userData.currentCultureId}
                    rules={[
                        { required: true, message: <Translate id="admin.common.requiredRule" /> },
                    ]}
                >
                    <Select allowClear={true}>
                        {Languages.All.filter(x => x.isAvailable).map(x => <Select.Option key={`cultureIds${x.id}`} value={x.id}>{x.name} ({x.code})</Select.Option>)}
                    </Select>
                </FieldDecorator>

                <FieldDecorator
                    form={this.props.form}
                    label={<Translate id="admin.universalUserSearch.columnHeader8" />}
                    name={NameOf<UpdateUserDTO>("isActive")}
                    initialValue={userData.isActive ? "true" : "false"}
                    rules={[
                        { required: true, message: <Translate id="admin.common.requiredRule" /> },
                    ]}
                    visible={statusVisible}
                >
                    <Select disabled={!statusEdit}>
                        <Select.Option key={"status-active"} value={"true"} title={"Active"}>Active</Select.Option>
                        <Select.Option key={"status-inactive"} value={"false"} title={"Inactive"}>Inactive</Select.Option>
                    </Select>
                </FieldDecorator>

                <Form.Item>
                    <Button type="primary" loading={this.props.isSubmitting} htmlType="submit">
                        {this.props.translate("admin.common.saveButton")}
                    </Button>
                    <Divider type="vertical" />
                    <Button onClick={this.handleCancel}>
                        {this.props.translate("common.back")}
                    </Button>
                </Form.Item>
            </Form>);
    }
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        User: state.User,
    };
}

export default connect(mapStateToProps)(withLocalize(Form.create()(AddEditUserForm)));



// WEBPACK FOOTER //
// ./src/pages/admin/forms/AddEditUserForm.tsx